import React, { useState, useEffect } from 'react';
import { UserTable, SearchFilter, Pagination } from '../components';
import { fetchUsers } from '../services/api';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 0,
    size: 10,
    totalPages: 0,
    totalElements: 0,
    last: false
  });
  
  const [filters, setFilters] = useState({
    email: '',
    isActive: null,
    sortBy: 'createdAt',
    sortDirection: 'desc'
  });

  // Fetch users when component mounts or when filters/pagination change
  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Prepare query parameters
        const params = {
          page: pagination.page,
          size: pagination.size,
          sortBy: filters.sortBy,
          sortDirection: filters.sortDirection
        };
        
        // Add optional filters if they exist
        if (filters.email) params.email = filters.email;
        if (filters.isActive !== null) params.isActive = filters.isActive;
        
        const response = await fetchUsers(params);
        
        setUsers(response.content);
        setPagination({
          ...pagination,
          totalPages: response.totalPages,
          totalElements: response.totalElements,
          last: response.last
        });
      } catch (err) {
        setError('Failed to fetch users. Please try again later.');
        console.error('Error fetching users:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, [pagination.page, pagination.size, filters]);

  const handlePageChange = (newPage) => {
    setPagination({
      ...pagination,
      page: newPage
    });
  };

  const handleFilterChange = (newFilters) => {
    // Reset to first page when filters change
    setPagination({
      ...pagination,
      page: 0
    });
    
    setFilters({
      ...filters,
      ...newFilters
    });
  };

  const handleRefresh = () => {
    // Rerun the current query to refresh data
    const currentPage = pagination.page;
    setPagination({
      ...pagination,
      page: -1 // Set to invalid value temporarily
    });
    // Then reset to current page to trigger the useEffect
    setTimeout(() => {
      setPagination({
        ...pagination,
        page: currentPage
      });
    }, 0);
  };

  return (
    <div className="px-2 sm:px-0">
      <div className="mb-6">
        <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">Genius Management</h2>
        <SearchFilter 
          onFilterChange={handleFilterChange} 
          initialFilters={filters} 
        />
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-4">
          <p>{error}</p>
        </div>
      )}
      
      <UserTable 
        users={users} 
        loading={loading}
        onRefresh={handleRefresh}
      />
      
      {!loading && users.length > 0 && (
        <Pagination 
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
        />
      )}
      
      <div className="mt-6 text-center text-sm text-gray-500">
        {!loading && users.length > 0 && (
          <p>Showing {users.length} of {pagination.totalElements} users</p>
        )}
      </div>
    </div>
  );
};

export default UserManagement; 